import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Linkedin
} from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();

  const productCategories = [
    /*'building',*/
    'electrical',
    'fixtures',
    /*'lighting',*/
    'stones',
    /*'steel',*/
    'sanitary',
    'tiles'
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-6">
              <img
                src="/Noor.png"
                alt="Noor Altayseer Logo"
                className="w-12 h-12 rounded-lg"
                style={{ objectFit: 'cover', background: 'white' }}
              />
              <div>
                <h3 className="text-2xl font-bold">
                  {language === 'en' ? 'Noor Altayseer' : 'نور التيسير'}
                </h3>
                <p className="text-gray-300 text-sm">
                  {language === 'en' ? 'Building & Construction' : 'البناء والتشييد'}
                </p>
              </div>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {language === 'en' 
                ? 'UAE\'s premier supplier of building materials and construction solutions. Quality, reliability, and innovation for your construction projects.'
                : 'المورد الرائد في دولة الإمارات لمواد البناء وحلول التشييد. الجودة والموثوقية والابتكار لمشاريعك الإنشائية.'
              }
            </p>
            <div className="flex space-x-4 rtl:space-x-reverse">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-yellow-500 transition-all duration-300 hover:scale-110"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-xl font-bold mb-6 text-yellow-400">
              {language === 'en' ? 'Quick Links' : 'روابط سريعة'}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {t('nav.products')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
              <li>
                <Link to="/quote" className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {t('nav.quote')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Product Categories */}
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-xl font-bold mb-6 text-yellow-400">
              {language === 'en' ? 'Products' : 'المنتجات'}
            </h3>
            <ul className="space-y-3">
              {productCategories.slice(0, 6).map((category) => (
                <li key={category}>
                  <Link 
                    to={`/products#${category}`}
                    className="text-gray-300 hover:text-yellow-400 transition-colors text-sm"
                  >
                    {t(`products.categories.${category}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-xl font-bold mb-6 text-yellow-400">
              {t('contact.title')}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 rtl:space-x-reverse">
                <MapPin className="w-5 h-5 text-yellow-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">
                    {language === 'en' 
                      ? 'Dubai, United Arab Emirates'
                      : 'دبي، الإمارات العربية المتحدة'
                    }
                  </p>
                </div>
              </li>
              <li className="flex items-center space-x-3 rtl:space-x-reverse">
                <Phone className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">+971 50 538 2246</span>
              </li>
              <li className="flex items-center space-x-3 rtl:space-x-reverse">
                <Mail className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">info@nooraltayseer.com</span>
              </li>
              <li className="flex items-start space-x-3 rtl:space-x-reverse">
                <Clock className="w-5 h-5 text-yellow-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">
                    {language === 'en' ? 'Sun - Thu: 8:00 AM - 6:00 PM' : 'الأحد - الخميس: 8:00 ص - 6:00 م'}
                  </p>
                  <p className="text-gray-300 text-sm">
                    {language === 'en' ? 'Saturday: 8:00 AM - 2:00 PM' : 'السبت: 8:00 ص - 2:00 م'}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              {language === 'en' 
                ? '© 2025 Noor Altayseer Building & Construction. All rights reserved.'
                : '© 2025 نور التيسير للبناء والتشييد. جميع الحقوق محفوظة.'
              }
            </p>
            <div className="flex space-x-6 rtl:space-x-reverse">
              <a href="#" className="text-gray-400 hover:text-yellow-400 text-sm transition-colors">
                {language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}
              </a>
              <a href="#" className="text-gray-400 hover:text-yellow-400 text-sm transition-colors">
                {language === 'en' ? 'Terms of Service' : 'شروط الخدمة'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;